import { useEffect, useRef, useState } from 'react';

import {
  APPROVAL_BLOCK_MESSAGES_AR,
  REVIEW_REASON_LABELS_AR,
  approvalBlock,
  isUsableReason,
  namesAgree,
} from '../features/committee/application/registrationReview';
import type { EmployeeCandidate } from '../features/committee/application/ports/registrationReviewPort';
import { Alert, Badge, Button, Card, TextField } from '../shared/components';

import styles from './ScreenLayout.module.css';
import reviewStyles from './RegistrationReviewPage.module.css';

/**
 * The queue of people waiting to be let in.
 *
 * 263 of the 872 imported employees have no work email, so they cannot be
 * matched automatically and land here. Until this screen existed the queue was
 * visible and nothing could be done about it.
 */

export interface PendingRow {
  readonly authUid: string;
  readonly fullNameAr: string;
  readonly email: string;
  readonly phone: string;
  readonly branchNameAr: string;
  readonly reason: 'name_mismatch' | 'no_matching_record';
  readonly submittedAtAr: string;
}

export interface RegistrationReviewPageProps {
  readonly pending: readonly PendingRow[];
  readonly busyUid: string | null;
  readonly errorAr: string | null;
  readonly noticeAr: string | null;
  readonly onLookUp: (employeeNumber: string) => Promise<EmployeeCandidate | null>;
  readonly onApprove: (applicantUid: string, employeeId: string) => void;
  readonly onReject: (applicantUid: string, reasonAr: string) => void;
}

export function RegistrationReviewPage({
  pending,
  busyUid,
  errorAr,
  noticeAr,
  onLookUp,
  onApprove,
  onReject,
}: RegistrationReviewPageProps) {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.heading}>طلبات التسجيل</h1>
          <p className={styles.lede}>
            من لم يُطابَق بريده تلقائيًا. راجع الاسم ثم اربطه بسجله أو ارفض الطلب.
          </p>
        </div>
      </div>

      {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}
      {noticeAr ? <Alert severity="success">{noticeAr}</Alert> : null}

      {pending.length === 0 ? (
        <Card>
          <p className={styles.body}>لا توجد طلبات بانتظار المراجعة.</p>
        </Card>
      ) : (
        pending.map((row) => (
          <PendingRequest
            key={row.authUid}
            row={row}
            busy={busyUid === row.authUid}
            onLookUp={onLookUp}
            onApprove={onApprove}
            onReject={onReject}
          />
        ))
      )}
    </div>
  );
}

function PendingRequest({
  row,
  busy,
  onLookUp,
  onApprove,
  onReject,
}: {
  readonly row: PendingRow;
  readonly busy: boolean;
  readonly onLookUp: (employeeNumber: string) => Promise<EmployeeCandidate | null>;
  readonly onApprove: (applicantUid: string, employeeId: string) => void;
  readonly onReject: (applicantUid: string, reasonAr: string) => void;
}) {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [candidate, setCandidate] = useState<EmployeeCandidate | null>(null);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [reasonAr, setReasonAr] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const block = approvalBlock(candidate);
  const mismatch = candidate !== null && !namesAgree(row.fullNameAr, candidate.fullName);

  /*
   * The search runs as the number is typed, debounced.
   *
   * It used to wait for a button, so a reviewer who typed the number found the
   * approve button dead with nothing saying why — the record had never been
   * looked up.
   *
   * Driven from the change event rather than an effect: the lookup is a
   * reaction to typing, not to rendering, and an effect that sets state on its
   * own is the shape React's lint rules refuse for good reason.
   */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const search = (raw: string) => {
    if (timer.current !== null) clearTimeout(timer.current);

    const wanted = raw.trim();
    if (wanted === '') {
      setCandidate(null);
      setSearched(false);
      setSearching(false);
      return;
    }

    setSearching(true);
    timer.current = setTimeout(() => {
      void onLookUp(wanted).then((found) => {
        setCandidate(found);
        setSearched(true);
        setSearching(false);
      });
    }, 400);
  };

  return (
    <Card title={row.fullNameAr} description={REVIEW_REASON_LABELS_AR[row.reason]}>
      <dl className={reviewStyles.facts}>
        <Fact labelAr="البريد" valueAr={row.email} />
        <Fact labelAr="الجوال" valueAr={row.phone} />
        <Fact labelAr="الفرع الذي ذكره" valueAr={row.branchNameAr} />
        <Fact labelAr="تاريخ الطلب" valueAr={row.submittedAtAr} />
      </dl>

      <div className={styles.formGrid}>
        <TextField
          label="الرقم الوظيفي في الكشف"
          numeric
          inputMode="numeric"
          value={employeeNumber}
          onChange={(event) => {
            setEmployeeNumber(event.target.value);
            search(event.target.value);
          }}
          hint="يُبحث عنه تلقائيًا أثناء الكتابة"
        />

        <div className={reviewStyles.lookupAction}>
          {searching ? <span className={reviewStyles.searching}>جارٍ البحث…</span> : null}
        </div>
      </div>

      {searched && !searching && candidate === null ? (
        <Alert severity="warning">لا يوجد سجل بهذا الرقم الوظيفي.</Alert>
      ) : null}

      {candidate ? (
        <div className={reviewStyles.candidate}>
          <div>
            <span className={reviewStyles.candidateName}>{candidate.fullName}</span>
            <span className={reviewStyles.candidateMeta}>
              {`الرقم الوظيفي: ${candidate.employeeNumber}`}
            </span>
          </div>

          <Badge tone={candidate.alreadyLinked ? 'error' : 'success'} withDot>
            {candidate.alreadyLinked ? 'مرتبط بحساب' : 'متاح'}
          </Badge>
        </div>
      ) : null}

      {/* Comparing the two names by hand is the entire purpose of this queue,
          so a difference is stated rather than left to be noticed. */}
      {mismatch ? (
        <Alert severity="warning" title="الاسمان غير متطابقين">
          {`كتب: ${row.fullNameAr} — وفي الكشف: ${candidate?.fullName ?? ''}`}
        </Alert>
      ) : null}

      {block !== null && searched && !searching ? (
        <Alert severity="info">{APPROVAL_BLOCK_MESSAGES_AR[block]}</Alert>
      ) : null}

      <div className={reviewStyles.decision}>
        <Button
          type="button"
          loading={busy}
          disabled={block !== null}
          onClick={() => {
            if (candidate) onApprove(row.authUid, candidate.id);
          }}
        >
          اعتماد وربط الحساب
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setRejecting((open) => !open);
          }}
        >
          رفض الطلب
        </Button>
      </div>

      {rejecting ? (
        <div className={reviewStyles.rejection}>
          <TextField
            label="سبب الرفض"
            required
            value={reasonAr}
            onChange={(event) => {
              setReasonAr(event.target.value);
            }}
            hint="يُعرض على مقدّم الطلب، فاذكر ما يستطيع تصحيحه"
          />

          <Button
            type="button"
            variant="danger"
            loading={busy}
            disabled={!isUsableReason(reasonAr)}
            onClick={() => {
              onReject(row.authUid, reasonAr);
            }}
          >
            تأكيد الرفض
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function Fact({ labelAr, valueAr }: { readonly labelAr: string; readonly valueAr: string }) {
  return (
    <div className={reviewStyles.fact}>
      <dt className={reviewStyles.factLabel}>{labelAr}</dt>
      <dd className={reviewStyles.factValue}>{valueAr}</dd>
    </div>
  );
}
